import { computed, ref } from 'vue'

import type { File } from '@/shared/graphql/router/graphql'
import { useOriginStore } from '@/shared/stores/origin'
import { openDialog, Dialog } from '@/shared/dialogs/dialog'

export function useRegisterCountries(files: File[]) {
  const originStore = useOriginStore()

  const isSingle = files.length === 1

  const registeredOriginIds = isSingle
    ? (files[0].copyright?.registrations ?? [])
        .map((registration) => registration.origin?.id)
        .filter((id): id is string => !!id)
    : []

  const lockedIds = new Set<string>(['Dacr', ...registeredOriginIds])

  const isLocked = (id: string) => lockedIds.has(id)

  const selectedIds = ref<Set<string>>(new Set(lockedIds))

  const lockedOrigins = computed(() =>
    originStore.origins.filter((origin) => isLocked(origin.id))
  )

  const selectedOrigins = computed(() =>
    originStore.origins.filter(
      (origin) => !isLocked(origin.id) && selectedIds.value.has(origin.id)
    )
  )

  const availableOrigins = computed(() =>
    originStore.origins.filter(
      (origin) => !isLocked(origin.id) && !selectedIds.value.has(origin.id)
    )
  )

  const newOriginIds = computed(() =>
    [...selectedIds.value].filter((id) => !isLocked(id))
  )

  const canSave = computed(() => newOriginIds.value.length > 0)

  const workNoun = computed(() => (isSingle ? 'this work' : 'these works'))

  const toggleOrigin = (id: string) => {
    if (isLocked(id)) return
    const next = new Set(selectedIds.value)
    if (next.has(id)) next.delete(id)
    else next.add(id)
    selectedIds.value = next
  }

  const proceed = () => {
    if (!canSave.value) return
    openDialog(Dialog.ConfirmRegisterCountries, {
      stack: 'cover',
      props: { files, originIds: newOriginIds.value },
    })
  }

  return {
    lockedOrigins,
    selectedOrigins,
    availableOrigins,
    isLocked,
    toggleOrigin,
    canSave,
    workNoun,
    proceed,
  }
}
